import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import styled from 'styled-components';
import { getCookie } from 'utils';

import { renderFormField } from 'modules/form';
import { studentUpgradeFieldsV2 } from 'constants/form';
import { Button, Row, Separator } from '@cuemath/comps';
import BallLoader from 'comps/ball-loader';
import { Message } from 'modules/form';

import {
  post as studentCourseUpgradeV2Post,
  get as studentCourseUpgradeV2Get,
  emptyStoreData as emptyStoreDataV2,
} from 'actions/student-course-upgrade-v2';
import { closeModal } from 'actions/modal';
import { Text } from '@cuemath/web-comps';
import CopyButton from '../../comps/copy-button';

const Wrapper = styled.div`
  padding: 40px 10px 20px;
`;

const ButtonWrapper = styled(Row)`
  justify-content: center;
`;

const CopyButtonWrapper = styled.div`
  width: 300px;
`;

const CopyButtonText = styled.div`
  width: 28%;
  padding-left: 10px;
`;

const StyledRow = styled(Row)`
  width: 80%;
`;

const getFormDefaults = () => {
  const formDefaults = {};

  studentUpgradeFieldsV2.forEach(field => {
    formDefaults[field.name] = field.default;
  });

  return formDefaults;
};

const StudentUpgradeV2Form = ({ studentCourseUpgradeV2, studentCourse, actions }) => {
  const [form, setForm] = useState(getFormDefaults());
  const [fields, setFields] = useState(studentUpgradeFieldsV2);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const { id: studentCourseId } = studentCourse || {};

  const studentCourseUpgradeV2PostData = store.post(
    studentCourseUpgradeV2,
    studentCourseId,
  );
  const studentCourseUpgradeV2GetData = store.get(
    studentCourseUpgradeV2,
    studentCourseId,
  );

  const { data, isLoaded } = studentCourseUpgradeV2GetData || {};
  const { resolved: postDataResolvedV2 } = studentCourseUpgradeV2PostData || {};
  const errorObjV2 = postDataResolvedV2 && store.getError(postDataResolvedV2);
  const { message: messageFromV2 } = errorObjV2 || {};
  const {
    data: postDataV2,
    isCreated: isV2PostCreated,
    isCreatingFailed: isV2PostCreatingFailed,
  } = postDataResolvedV2 || {};
  const { upgrade_details: upgradeDetails, config } = data || {};

  const { pending_amount: discountAmount, pending_classes: pendingClasses } =
    upgradeDetails || {};
  const { upgrade_details: postUpgradeDetails } = postDataV2 || {};
  const { link: upgradeLink } = postUpgradeDetails || {};

  const resetForm = formToReset => {
    const newForm = form;
    for (let key in newForm) {
      if (formToReset[key]) {
        newForm[key] = '';
      }
    }
    setForm(newForm);
  };

  const fillFields = (type, data) => {
    const newFields =
      fields &&
      fields.map(field => {
        if (field.name === type && data) {
          const modifiedChoices = data.map(choice => {
            const label = choice;
            const value = choice;
            return { label, value };
          });
          return { ...field, choices: modifiedChoices, disabled: false };
        }

        if (type === 'class_ratio' && field.name === 'classes_per_week') {
          return { ...field, choices: [], disabled: true };
        }

        return field;
      });

    setFields(newFields);
  };
  const fillClassPerWeek = (items, enrollmentType, classRatio) => {
    const targetClassesPerWeek = new Set();
    items.forEach(item => {
      if (
        item.target_enrollment_type.includes(enrollmentType) &&
        item.target_class_ratio.includes(classRatio)
      ) {
        item.target_classes_per_week.forEach(classes => {
          targetClassesPerWeek.add(classes);
        });
      }
    });

    fillFields('classes_per_week', Array.from(targetClassesPerWeek));
  };

  const fillClassRatio = (items, enrollmentType) => {
    const targetClassRatios = new Set();
    items.forEach(item => {
      if (item.target_enrollment_type.includes(enrollmentType)) {
        item.target_class_ratio.forEach(ratio => {
          targetClassRatios.add(ratio);
        });
      }
    });

    fillFields('class_ratio', Array.from(targetClassRatios));
  };

  const fillEnrollmentTypes = items => {
    const uniqueEnrollmentTypes = new Set();
    items.forEach(item => {
      item.target_enrollment_type.forEach(type => {
        uniqueEnrollmentTypes.add(type);
      });
    });

    fillFields('to_enrollment_type', Array.from(uniqueEnrollmentTypes));
  };

  const fillForm = data => {
    if (!data) return;
    let newForm = {};
    const { pending_amount: discountAmount, pending_classes: pendingClasses } =
      data || {};

    if (discountAmount !== undefined && pendingClasses !== undefined) {
      newForm.discount_amount = discountAmount.toString();
      newForm.pending_classes = pendingClasses.toString();
    }

    setForm({ ...form, ...newForm });
    fillEnrollmentTypes(config);
  };

  const onOptionChange = (key, value) => {
    let valueToUpdate = value;

    if (key === 'to_enrollment_type') {
      resetForm({ class_ratio: true, classes_per_week: true });
      fillClassRatio(config, value);
    } else if (key === 'class_ratio') {
      resetForm({ class_ratio: true, classes_per_week: true });
      fillClassPerWeek(config, form.to_enrollment_type, value);
    }

    const newForm = { ...form };
    newForm[key] = valueToUpdate;

    setError('');
    setForm(newForm);
  };

  const onConfirm = () => {
    const actionData = {
      to_class_ratio: form.class_ratio,
      actor_id: getCookie('user_id') || getCookie('emp_id'),
      to_classes_per_week: form.classes_per_week,
      to_enrollment_type: form.to_enrollment_type,
    };

    const postData = {
      action: 'get_or_create_upgrade_v2',
      action_data: actionData,
    };
    setLoading(true);
    actions.studentCourseUpgradeV2Post(studentCourseId, postData);
  };

  useEffect(() => {
    if (studentCourseId) {
      actions.studentCourseUpgradeV2Get(studentCourseId);

      setLoading(true);
    }
    return () => {
      actions.emptyStoreDataV2({ items: {} });
    };
  }, []);

  useEffect(() => {
    if (studentCourseId) {
      if (isLoaded) {
        if ((!discountAmount && !pendingClasses) || !pendingClasses) {
          const newFields = fields && fields.map(field => ({ ...field, disabled: true }));
          setFields(newFields);
        } else {
          fillForm(upgradeDetails);
          // fillFields(data);
        }
        setLoading(false);
      }
    }
  }, [isLoaded]);

  useEffect(() => {
    if (studentCourseId) {
      if (isV2PostCreated) {
        setSuccess(true);
      } else if (isV2PostCreatingFailed) {
        if (messageFromV2) {
          setError(messageFromV2);
        } else {
          setError('Something went wrong!');
        }
        setLoading(false);
      }
    }
  }, [isV2PostCreated, isV2PostCreatingFailed]);

  const isButtonDisabled =
    !form.to_enrollment_type || !form.class_ratio || !form.classes_per_week;

  return (
    <Wrapper>
      {fields &&
        fields.map(field =>
          renderFormField({
            ...field,
            width: '300px',
            value: form[field.name],
            onChange: onOptionChange,
          }),
        )}

      {error && (
        <Message width="100%" error>
          {error}
        </Message>
      )}
      {success ? (
        <StyledRow alignItems="center" justifyContent="space-between">
          <CopyButtonText>
            <Text variant="body1regular">Upgrade Link</Text>
          </CopyButtonText>
          <CopyButtonWrapper>
            <CopyButton
              style={{ width: '300' }}
              copyMessage="Upgrade Link"
              text={upgradeLink + '?utm_source=la'}
            />
          </CopyButtonWrapper>
        </StyledRow>
      ) : (
        <>
          <Separator transparent height="20" />
          <ButtonWrapper>
            {loading ? (
              <BallLoader />
            ) : (
              <Button
                align="center"
                label="Submit"
                onClick={onConfirm}
                disabled={isButtonDisabled}
              />
            )}
          </ButtonWrapper>
        </>
      )}
    </Wrapper>
  );
};

export default connect(
  ({ studentCourseUpgradeV2 }) => ({
    studentCourseUpgradeV2,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        studentCourseUpgradeV2Post,
        studentCourseUpgradeV2Get,
        emptyStoreDataV2,
        closeModal,
      },
      dispatch,
    ),
  }),
)(StudentUpgradeV2Form);
